import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_brillien-project/artifacts/dfytjwbe_logo.JPG";

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, user } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // Redirect if already logged in
  if (user) {
    navigate("/dashboard");
    return null;
  }

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await login(email, password);
    
    if (result.success) {
      toast.success("Login successful!");
      navigate("/dashboard");
    } else {
      toast.error(result.error || "Login failed");
    }
    
    setLoading(false);
  };

  return (
    <div className="flex h-screen">
      {/* Left Panel - Hero Section */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-gradient-to-br from-brillien-600 via-brillien-700 to-brillien-800">
        {/* Decorative Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-64 h-64 border border-white rounded-full" />
          <div className="absolute top-40 left-40 w-96 h-96 border border-white rounded-full" />
          <div className="absolute bottom-20 right-20 w-48 h-48 border border-white rounded-full" />
        </div>
        
        <div className="absolute inset-0 flex flex-col justify-between p-12">
          {/* Logo in Hero */}
          <div className="flex items-center gap-3">
            <img 
              src={LOGO_URL} 
              alt="Brillien Logo" 
              className="h-12 w-auto bg-white rounded-lg p-1"
            />
          </div>
          
          {/* Hero Text */}
          <div className="text-white">
            <h2 className="font-heading text-4xl font-bold tracking-tight mb-4">
              Building Excellence,<br />One Project at a Time
            </h2>
            <p className="text-brillien-100 text-lg max-w-md">
              Comprehensive project management for construction and space engineering professionals.
            </p>
          </div>
          
          {/* Stats */}
          <div className="flex gap-12">
            <div>
              <p className="text-3xl font-bold text-white">500+</p>
              <p className="text-brillien-200 text-sm">Projects Delivered</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-white">50+</p>
              <p className="text-brillien-200 text-sm">Expert Team</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-white">98%</p>
              <p className="text-brillien-200 text-sm">Client Satisfaction</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <Card className="w-full max-w-md p-8 border-slate-200 rounded-lg shadow-lg">
          {/* Mobile Logo */}
          <div className="lg:hidden flex justify-center mb-6">
            <img 
              src={LOGO_URL} 
              alt="Brillien Logo" 
              className="h-14 w-auto"
            />
          </div>
          
          <div className="mb-8">
            <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900 mb-2">
              Welcome Back
            </h1>
            <p className="text-sm text-slate-600">
              Sign in to access your CRM workspace
            </p>
          </div>

          <form onSubmit={handleLogin} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                Email Address
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="you@brillien.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                className="h-11 rounded-lg border-slate-300 focus:border-brillien-500 focus:ring-brillien-500"
                required
                disabled={loading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium text-slate-700">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password-input"
                  className="h-11 rounded-lg border-slate-300 focus:border-brillien-500 focus:ring-brillien-500 pr-10"
                  required
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              data-testid="login-submit-button"
              className="w-full h-11 bg-brillien-600 hover:bg-brillien-700 text-white rounded-lg font-medium transition-colors"
              disabled={loading}
            >
              {loading ? "Signing in..." : "Sign In"}
            </Button>
          </form>
          
          {/* Footer */}
          <div className="mt-8 text-center">
            <p className="text-xs text-slate-500">
              © 2026 Brillien. All rights reserved.
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
}
